import { useState, useEffect } from 'react';
import { Divider, Paper } from '@mui/material';
import { HeroType } from '../types';
import { HeroContentSection } from '../components/Start/HeroContentSection';
import { ModuleOrderSection } from '../components/Start/ModuleOrderSection';
import { useHeroVideo } from '../hooks/useHeroVideo';
import { useVideoCarousels } from '../hooks/useVideoCarousels';
import { useFotbollPlayTeamChannels } from '../hooks/useFotbollPlayTeamChannels';
import { useFotbollPlaySubmit } from '../api/FotbollPlaySubmitProvider';
import type { ModuleOrderEntry, StartTabPayload } from '../api/types';
import { AVAILABLE_MODULE_CATEGORIES, FOTBOLL_PLAY_MODULE_CATEGORY_LABELS } from '../constants';
import {
  normalizeModuleOrder,
  prepareModuleOrderForSave,
  sanitizeModuleOrder,
  toChannelId,
  updateCategoryChannelIds,
  updateCustomCarouselId,
  updateModuleTitle,
} from '../utils/module-order-utils';

interface StartTabProps {
  teamAbbrv: string;
  config?: StartTabPayload;
}

function getInitialModuleOrder(config?: StartTabPayload): ModuleOrderEntry[] {
  const legacyConfig = config as StartTabPayload & {
    customCarouselSelections?: Record<string, string>;
    categoryChannelSelections?: Record<string, string | string[]>;
  };

  return normalizeModuleOrder(
    config?.moduleOrder,
    legacyConfig?.customCarouselSelections,
    legacyConfig?.categoryChannelSelections
  );
}

export function StartTab({ teamAbbrv, config }: StartTabProps) {
  const [heroType, setHeroType] = useState<HeroType>(config?.heroType ?? 'image');
  const [heroImage, setHeroImage] = useState(config?.heroImage ?? '');
  const [moduleOrder, setModuleOrder] = useState<ModuleOrderEntry[]>(getInitialModuleOrder(config));

  const { carousels } = useVideoCarousels();
  const { channelOptions, loading: areChannelsLoading } = useFotbollPlayTeamChannels(teamAbbrv);
  const heroVideoHook = useHeroVideo({ teamAbbrv });
  const { setHeroVideo } = heroVideoHook;
  const { registerTabPayload } = useFotbollPlaySubmit();

  useEffect(() => {
    setHeroType(config?.heroType ?? 'image');
    setHeroImage(config?.heroImage ?? '');
    setHeroVideo(config?.heroVideo ?? null);
    setModuleOrder(getInitialModuleOrder(config));
  }, [config, setHeroVideo]);

  useEffect(() => {
    return registerTabPayload(1, () => ({
      heroType,
      heroImage,
      heroVideo: heroVideoHook.heroVideo,
      moduleOrder: prepareModuleOrderForSave(
        moduleOrder,
        FOTBOLL_PLAY_MODULE_CATEGORY_LABELS,
        carousels
      ),
    }));
  }, [carousels, heroImage, heroType, heroVideoHook.heroVideo, moduleOrder, registerTabPayload]);

  useEffect(() => {
    if (areChannelsLoading) return;

    const carouselIds = new Set(carousels.map((carousel) => carousel.id));
    const channelIds = new Set(
      channelOptions
        .map((channel) => toChannelId(channel.id))
        .filter((id): id is number => id !== null)
    );
    setModuleOrder((prev) => {
      const next = sanitizeModuleOrder(prev, carouselIds, channelIds);
      return next === prev ? prev : next;
    });
  }, [areChannelsLoading, carousels, channelOptions]);

  function handleEnded() {
    if (heroVideoHook.videoRef.current && heroVideoHook.heroVideo) {
      heroVideoHook.videoRef.current.currentTime = heroVideoHook.heroVideo.loopStart;
      void heroVideoHook.videoRef.current.play().catch((err: DOMException) => {
        if (err.name !== 'AbortError') {
          console.error('Failed to replay hero video preview:', err);
        }
      });
    }
  }

  return (
    <Paper variant="outlined" sx={{ mt: 3, mx: 3, p: 0 }}>
      <HeroContentSection
        teamAbbrv={teamAbbrv}
        heroType={heroType}
        heroImage={heroImage}
        heroVideo={heroVideoHook.heroVideo}
        heroVideoSearchQuery={heroVideoHook.heroVideoSearchQuery}
        heroVideoSearchResults={heroVideoHook.heroVideoSearchResults}
        isSearchingHeroVideo={heroVideoHook.isSearchingHeroVideo}
        isPreviewPlaying={heroVideoHook.isPreviewPlaying}
        actualVideoDuration={heroVideoHook.actualVideoDuration}
        videoRef={heroVideoHook.videoRef}
        videoUrl={heroVideoHook.videoUrl}
        onHeroTypeChange={setHeroType}
        onHeroImageChange={setHeroImage}
        onHeroVideoSearchQueryChange={heroVideoHook.setHeroVideoSearchQuery}
        onSelectHeroVideo={heroVideoHook.handleSelectHeroVideo}
        onRemoveHeroVideo={heroVideoHook.handleRemoveHeroVideo}
        onPausePreview={heroVideoHook.handlePausePreview}
        onPlayPreview={heroVideoHook.handlePlayPreview}
        onLoopRangeChange={heroVideoHook.handleLoopRangeChange}
        onVideoTimeUpdate={heroVideoHook.handleVideoTimeUpdate}
        onLoadedMetadata={heroVideoHook.handleLoadedMetadata}
        onEnded={handleEnded}
        getEffectiveDuration={heroVideoHook.getEffectiveDuration}
      />
      <Divider />
      <ModuleOrderSection
        moduleOrder={moduleOrder}
        carousels={carousels}
        channels={channelOptions}
        availableCategories={AVAILABLE_MODULE_CATEGORIES}
        categoryLabels={FOTBOLL_PLAY_MODULE_CATEGORY_LABELS}
        onModuleOrderChange={setModuleOrder}
        onCustomCarouselSelectionChange={(moduleKey, carouselId) =>
          setModuleOrder((prev) => updateCustomCarouselId(prev, moduleKey, carouselId, carousels))
        }
        onCategoryChannelSelectionChange={(moduleKey, channelIds) =>
          setModuleOrder((prev) => updateCategoryChannelIds(prev, moduleKey, channelIds))
        }
        onModuleTitleChange={(moduleKey, title) =>
          setModuleOrder((prev) => updateModuleTitle(prev, moduleKey, title))
        }
      />
    </Paper>
  );
}
